import type { FormApi } from '../../../form-hooks/useFormApi';
import type {
  ContentObject,
  ContentObjectVersion,
  ContentTypeDefinition,
} from '../../../lib/flotiq-client/types/api-response';

import { use, useCallback, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import { useContentObjectVersion } from '../../../hooks/api';
import useApiErrorsToast from '../../../hooks/api/useApiErrorsToast';

import { ModalInstanceContext } from '../../../contexts/ModalContext';

import { ReloadIcon } from '../../../images/shapes';

import VersionContentObjectForm from '../../../form/VersionContentObjectForm/VersionContentObjectForm';
import Button from '../../Button/Button';
import CancelButton from '../../Button/predefined/CancelButton/CancelButton';
import Loader from '../../Loader/Loader';
import VersionPill from '../VersionPill/VersionPill';

const TabButton = ({
  selected,
  onClick,
  isCurrent,
}: {
  selected: boolean;
  onClick: (isCurrent: boolean) => void;
  isCurrent?: boolean;
}) => {
  const { t } = useTranslation();

  const handleClick = useCallback(() => {
    onClick(!isCurrent);
  }, [isCurrent, onClick]);

  return (
    <button
      className={twMerge(
        'font-semibold uppercase outline-0',
        'mx-1 px-1 py-2 md:mx-2 md:px-7 md:py-4',
        'text-sm md:text-base whitespace-nowrap',
        'border-b-[3px] border-transparent -mb-0.5 dark:text-slate-200',
        selected && 'border-blue text-blue dark:text-blue',
      )}
      onClick={handleClick}
    >
      {isCurrent ? t('Versions.CurrentVersion') : t('Versions.OldVersion')}
    </button>
  );
};

export interface VersionDiffModalContentProps {
  /**
   * Object ID to compare versions for
   */
  objectId: string;
  /**
   * Selected version to compare
   */
  selectedVersion: ContentObjectVersion;
  /**
   * Current form changes
   */
  currentChanges: Record<string, any>;
  /**
   * Content type for object
   */
  contentType: ContentTypeDefinition | null;
  /**
   * Latest version information
   */
  latestVersion?: ContentObject;
  /**
   * Test id for information container
   */
  testId?: string;
}

const VersionDiffModalContent = ({
  objectId,
  selectedVersion,
  latestVersion,
  currentChanges,
  contentType,
  testId = '',
}: VersionDiffModalContentProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);
  const [oldObjectFormState, setOldObjectFormState] = useState<FormApi>();

  const [isCurrentTabSelected, toggleTab] = useReducer(
    (isCurrent) => !isCurrent,
    false,
  );

  const closeModal = useCallback(() => {
    modalInstance.resolve?.();
  }, [modalInstance]);

  const {
    entity: ctoVersion,
    errors: contentVersionErrors,
    isLoading: ctoVersionLoading,
  } = useContentObjectVersion(objectId, selectedVersion);

  useApiErrorsToast(contentVersionErrors);

  const restoreVersion = useCallback(async () => {
    if (!oldObjectFormState) return;
    const oldObject = oldObjectFormState.getValues();
    modalInstance.resolve?.(oldObject);
  }, [modalInstance, oldObjectFormState]);

  return (
    <>
      <div
        className={`absolute w-full z-10 top-0 left-0 py-1 md:py-3 pl-2 pr-6 md:pl-11 md:pr-14 
          bg-white dark:bg-slate-900 text-base md:border-b border-slate-500 dark:border-slate-300
          grid grid-cols-1 md:grid-cols-3 justify-center items-center`}
      >
        <div className="hidden md:flex items-center">
          <VersionPill status={selectedVersion.internal.status} />
          <span className="ml-1 md:ml-4">
            {moment(selectedVersion.internal.updatedAt).format(
              `L | LT | [${
                !selectedVersion.editor
                  ? 'N/A'
                  : selectedVersion.editor?.firstName +
                    ' ' +
                    selectedVersion.editor?.lastName
              }]`,
            )}
          </span>
        </div>
        <h2 className="mx-auto">{t('Versions.VersionDiff')}</h2>
        <div className="hidden md:flex ml-auto">
          {!latestVersion ? (
            t('Versions.CurrentChanges')
          ) : (
            <>
              <span className="mr-4">{t('Versions.CurrentVersion')}</span>
              <VersionPill status={latestVersion.internal?.status} />
            </>
          )}
        </div>

        <div
          className={`md:hidden w-full border-b dark:border-slate-800 
            grid grid-cols-2 justify-center scrollbar-sm scrollbar-hidden`}
        >
          <TabButton selected={!isCurrentTabSelected} onClick={toggleTab} />
          <TabButton
            selected={isCurrentTabSelected}
            onClick={toggleTab}
            isCurrent
          />
        </div>
      </div>

      {ctoVersionLoading ? (
        <div className="w-full h-64 flex items-center justify-center mt-20">
          <Loader size="normal" type="spinner-grid" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 xl:gap-x-16 gap-y-4 mt-20">
          {ctoVersion ? (
            <>
              <VersionContentObjectForm
                oldContentObject={ctoVersion}
                currentContentObject={currentChanges}
                contentType={contentType}
                selected={!isCurrentTabSelected}
                setFormState={setOldObjectFormState}
              />
              <VersionContentObjectForm
                oldContentObject={ctoVersion}
                currentContentObject={currentChanges}
                contentType={contentType}
                selected={isCurrentTabSelected}
                isCurrent
              />
            </>
          ) : (
            t('Versions.VersionNotFound')
          )}
        </div>
      )}

      <div
        className={twMerge(
          'w-full absolute left-0 bottom-0 flex items-center justify-center p-3 md:px-10 space-x-5',
          'border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 z-10',
        )}
      >
        <CancelButton onClick={closeModal} />
        <Button
          iconImage={<ReloadIcon className="w-3 min-w-3" />}
          iconPosition="start"
          onClick={restoreVersion}
          disabled={!ctoVersion}
          type="button"
          {...getTestProps(testId, 'restore', 'testId')}
        >
          {t('Versions.RestoreVersion')}
        </Button>
      </div>
    </>
  );
};

export default VersionDiffModalContent;
